import type { CrremDashboardPayload } from "features/crrem-pathway-chart/model/types";

import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type {
  CrremSettings,
  EnergyAnalysisDimension,
  EnergyAnalysisRow,
  EnergyAnalysisViewModel,
} from "../model/types";

import {
  buildChangeWaterfallOption,
  buildComparisonDoughnutOption,
  buildCorrectionImpactOption,
  buildCorrectionScenariosTrendOption,
  buildCorrectionWaterfallOption,
  buildScenarioHeatmapOption,
} from "../lib/buildInsightOptions";
import { ENERGY_ANALYSIS_DIMENSION } from "../model/constants";
import { EnergyAnalysisDimensionInsights } from "./EnergyAnalysisDimensionInsights";
import { EnergyAnalysisInsightChart } from "./EnergyAnalysisInsightChart";
import { EnergyAnalysisJointCharts } from "./EnergyAnalysisJointCharts";
import { EnergyAnalysisMixInsights } from "./EnergyAnalysisMixInsights";

type EnergyAnalysisInsightsProps = {
  dimension: EnergyAnalysisDimension;
  payload?: CrremDashboardPayload;
  rows: EnergyAnalysisRow[];
  settings?: CrremSettings;
  viewModel: EnergyAnalysisViewModel;
};

export const EnergyAnalysisInsights = ({
  dimension,
  payload,
  rows,
  settings,
  viewModel,
}: EnergyAnalysisInsightsProps) => {
  const { t } = useTranslation();
  const useTypeWaterfall = useMemo(
    () => buildChangeWaterfallOption(rows, "useType", viewModel.unit),
    [rows, viewModel.unit]
  );
  const useTypeComparison = useMemo(
    () => buildComparisonDoughnutOption(rows, "useType", viewModel.unit),
    [rows, viewModel.unit]
  );
  const utilityComparison = useMemo(
    () => buildComparisonDoughnutOption(rows, "utility", viewModel.unit),
    [rows, viewModel.unit]
  );
  const utilityWaterfall = useMemo(
    () => buildChangeWaterfallOption(rows, "utility", viewModel.unit),
    [rows, viewModel.unit]
  );
  const correctionWaterfall = useMemo(
    () =>
      payload && settings
        ? buildCorrectionWaterfallOption(payload, settings, viewModel.unit, viewModel.referenceYear)
        : {},
    [payload, settings, viewModel.referenceYear, viewModel.unit]
  );
  const correctionImpact = useMemo(
    () =>
      payload && settings
        ? buildCorrectionImpactOption(payload, settings, viewModel.referenceYear)
        : {},
    [payload, settings, viewModel.referenceYear]
  );
  const scenarioHeatmap = useMemo(
    () => (payload && settings ? buildScenarioHeatmapOption(payload, settings) : {}),
    [payload, settings]
  );
  const correctionScenariosTrend = useMemo(
    () =>
      payload && settings
        ? buildCorrectionScenariosTrendOption(payload, settings, viewModel.unit, viewModel.years)
        : {},
    [payload, settings, viewModel.unit, viewModel.years]
  );

  if (dimension === ENERGY_ANALYSIS_DIMENSION.joint) {
    return (
      <EnergyAnalysisJointCharts
        payload={payload}
        settings={settings}
        snapshotYear={viewModel.referenceYear}
        years={viewModel.years}
      />
    );
  }

  if (dimension === ENERGY_ANALYSIS_DIMENSION.mix) {
    return (
      <EnergyAnalysisMixInsights
        rows={rows}
        snapshotYear={viewModel.referenceYear}
        unit={viewModel.unit}
      />
    );
  }

  if (dimension === ENERGY_ANALYSIS_DIMENSION.utility) {
    return (
      <div className="grid w-full grid-cols-2 items-stretch gap-4">
        <EnergyAnalysisInsightChart
          option={utilityWaterfall}
          title={t("BuildingProfile.energyAnalysis.insights.changeDrivers")}
        />
        <EnergyAnalysisInsightChart
          height="26rem"
          option={utilityComparison}
          title={t("BuildingProfile.energyAnalysis.insights.startEndSources")}
        />
        <EnergyAnalysisDimensionInsights
          dimension="utility"
          rows={rows}
          snapshotYear={viewModel.referenceYear}
          unit={viewModel.unit}
        />
      </div>
    );
  }

  if (dimension === ENERGY_ANALYSIS_DIMENSION.useType) {
    return (
      <div className="grid w-full grid-cols-2 items-stretch gap-4">
        <EnergyAnalysisInsightChart
          option={useTypeWaterfall}
          title={t("BuildingProfile.energyAnalysis.insights.changeDrivers")}
        />
        <EnergyAnalysisInsightChart
          height="26rem"
          option={useTypeComparison}
          title={t("BuildingProfile.energyAnalysis.insights.startEndComposition")}
        />
        <EnergyAnalysisDimensionInsights
          dimension="useType"
          rows={rows}
          snapshotYear={viewModel.referenceYear}
          unit={viewModel.unit}
        />
      </div>
    );
  }

  if (dimension === ENERGY_ANALYSIS_DIMENSION.corrections) {
    return (
      <div className="grid w-full grid-cols-2 items-stretch gap-4">
        <EnergyAnalysisInsightChart
          height="32rem"
          iconName="Barchart"
          option={correctionScenariosTrend}
          title={t("BuildingProfile.energyAnalysis.insights.correctionScenariosTrend")}
        />
        <EnergyAnalysisInsightChart
          height="34rem"
          option={correctionWaterfall}
          title={t("BuildingProfile.energyAnalysis.insights.correctionWaterfall")}
        />
        <EnergyAnalysisInsightChart
          height="30rem"
          option={scenarioHeatmap}
          title={t("BuildingProfile.energyAnalysis.insights.scenarioPerformance")}
        />
        <EnergyAnalysisInsightChart
          height="32rem"
          option={correctionImpact}
          title={t("BuildingProfile.energyAnalysis.insights.correctionImpact")}
        />
      </div>
    );
  }

  return null;
};
